import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { parseEntityRef, stringifyEntityRef } from "@backstage/catalog-model";
import {
    Content,
    ContentHeader,
    SupportButton,
} from "@backstage/core-components";
import { useRouteRef } from "@backstage/core-plugin-api";
import {
    CatalogFilterLayout,
    EntityKindPicker,
    EntityLifecyclePicker,
    EntityListProvider,
    EntityOwnerPicker,
    EntitySearchBar,
    EntityTagPicker,
    UserListPicker,
} from "@backstage/plugin-catalog-react";
import scaffolderPlugin from "@backstage/plugin-scaffolder/alpha";
import type { TemplateEntityV1beta3 } from "@backstage/plugin-scaffolder-common";
import {
    TemplateCard,
    type TemplateCardProps,
    TemplateCategoryPicker,
    TemplateGroups,
} from "@backstage/plugin-scaffolder-react/alpha";

import { DeprecatedAwareTemplateCard } from "./DeprecatedAwareTemplateCard";
import { ExperimentalAwareTemplateCard } from "./ExperimentalAwareTemplateCard";

function LifecycleAwareTemplateCard(props: TemplateCardProps) {
    switch (props.template.spec.lifecycle) {
        case "deprecated":
            return <DeprecatedAwareTemplateCard {...props} />;
        case "experimental":
            return <ExperimentalAwareTemplateCard {...props} />;
        default:
            return (
                <TemplateCard
                    {...props}
                    onSelected={
                        props.onSelected
                            ? () => props.onSelected?.(props.template)
                            : undefined
                    }
                />
            );
    }
}

export function TemplatesWithLifecyclePicker() {
    const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
    const navigate = useNavigate();
    const groups = [{ title: "Templates", filter: () => true }];
    const onTemplateSelected = useCallback(
        (template: TemplateEntityV1beta3) => {
            const { namespace, name } = parseEntityRef(
                stringifyEntityRef(template)
            );
            navigate(templateRoute({ namespace, templateName: name }));
        },
        [navigate, templateRoute]
    );
    return (
        <EntityListProvider>
            <Content>
                <ContentHeader title="">
                    <SupportButton>
                        Create new software components using standard templates
                        in your organization.
                    </SupportButton>
                </ContentHeader>
                <CatalogFilterLayout>
                    <CatalogFilterLayout.Filters>
                        <EntitySearchBar />
                        <EntityKindPicker initialFilter="template" hidden />
                        <UserListPicker
                            initialFilter="all"
                            availableFilters={["all", "starred"]}
                        />
                        <TemplateCategoryPicker />
                        <EntityLifecyclePicker initialFilter={["production"]} />
                        <EntityTagPicker />
                        <EntityOwnerPicker />
                    </CatalogFilterLayout.Filters>
                    <CatalogFilterLayout.Content>
                        <TemplateGroups
                            groups={groups}
                            onTemplateSelected={onTemplateSelected}
                            TemplateCardComponent={LifecycleAwareTemplateCard}
                        />
                    </CatalogFilterLayout.Content>
                </CatalogFilterLayout>
            </Content>
        </EntityListProvider>
    );
}
